/**
 * Offline Storage Service using IndexedDB
 *
 * Provides persistent offline storage for tasks, boards, and sync queue
 * with automatic expiration and cleanup
 */

const DB_NAME = 'neokanban-offline';
const DB_VERSION = 1;

// Store names
export const STORES = {
  TASKS: 'tasks',
  BOARDS: 'boards',
  PROJECTS: 'projects',
  SYNC_QUEUE: 'sync_queue',
  METADATA: 'metadata',
} as const;

type StoreName = (typeof STORES)[keyof typeof STORES];

// Task stored offline
export interface OfflineTask {
  id: string;
  boardId: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  position: number;
  dueDate?: string;
  assigneeId?: string;
  labels?: string[];
  isOffline: boolean; // Created while offline
  localUpdatedAt: number; // Local timestamp for conflict resolution
  syncedAt?: number; // When last synced with server
}

// Board stored offline
export interface OfflineBoard {
  id: string;
  projectId: string;
  name: string;
  columns: Array<{
    id: string;
    name: string;
    position: number;
    taskIds: string[];
  }>;
  localUpdatedAt: number;
  syncedAt?: number;
}

// Project stored offline
export interface OfflineProject {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  boardIds: string[];
  localUpdatedAt: number;
  syncedAt?: number;
}

// Sync queue item
export interface SyncQueueItem {
  id: string;
  type: 'create' | 'update' | 'delete';
  entityType: 'task' | 'board' | 'project' | 'comment';
  entityId: string;
  payload: Record<string, unknown>;
  createdAt: number;
  retryCount: number;
  lastError?: string;
}

// Database instance
let db: IDBDatabase | null = null;

/**
 * Initialize the IndexedDB database
 */
export async function initOfflineStorage(): Promise<IDBDatabase> {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Tasks store with indexes
      if (!database.objectStoreNames.contains(STORES.TASKS)) {
        const taskStore = database.createObjectStore(STORES.TASKS, {
          keyPath: 'id',
        });
        taskStore.createIndex('boardId', 'boardId', { unique: false });
        taskStore.createIndex('isOffline', 'isOffline', { unique: false });
        taskStore.createIndex('localUpdatedAt', 'localUpdatedAt', { unique: false });
      }

      // Boards store
      if (!database.objectStoreNames.contains(STORES.BOARDS)) {
        const boardStore = database.createObjectStore(STORES.BOARDS, {
          keyPath: 'id',
        });
        boardStore.createIndex('projectId', 'projectId', { unique: false });
      }

      // Projects store
      if (!database.objectStoreNames.contains(STORES.PROJECTS)) {
        const projectStore = database.createObjectStore(STORES.PROJECTS, {
          keyPath: 'id',
        });
        projectStore.createIndex('organizationId', 'organizationId', { unique: false });
      }

      // Sync queue store
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: 'id',
        });
        syncStore.createIndex('createdAt', 'createdAt', { unique: false });
        syncStore.createIndex('entityType', 'entityType', { unique: false });
      }

      // Metadata store for app state
      if (!database.objectStoreNames.contains(STORES.METADATA)) {
        database.createObjectStore(STORES.METADATA, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Get the database instance, initializing if needed
 */
async function getDb(): Promise<IDBDatabase> {
  if (!db) {
    await initOfflineStorage();
  }
  return db!;
}

/**
 * Generic get item from store
 */
export async function getItem<T>(
  storeName: StoreName,
  key: string
): Promise<T | undefined> {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic set item in store
 */
export async function setItem<T>(storeName: StoreName, item: T): Promise<void> {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Generic delete item from store
 */
export async function deleteItem(
  storeName: StoreName,
  key: string
): Promise<void> {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all items from store
 */
export async function getAllItems<T>(storeName: StoreName): Promise<T[]> {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get items by index
 */
export async function getItemsByIndex<T>(
  storeName: StoreName,
  indexName: string,
  value: IDBValidKey
): Promise<T[]> {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);

    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all items from a store
 */
export async function clearStore(storeName: StoreName): Promise<void> {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Bulk insert items
 */
export async function bulkSetItems<T>(
  storeName: StoreName,
  items: T[]
): Promise<void> {
  const database = await getDb();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    for (const item of items) {
      store.put(item);
    }
  });
}

// ============================================================================
// Task-specific operations
// ============================================================================

/**
 * Save a task to offline storage
 */
export async function saveTaskOffline(task: OfflineTask): Promise<void> {
  await setItem(STORES.TASKS, {
    ...task,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Get a single task from offline storage
 */
export async function getTaskOffline(taskId: string): Promise<OfflineTask | undefined> {
  return getItem<OfflineTask>(STORES.TASKS, taskId);
}

/**
 * Delete a task from offline storage
 */
export async function deleteTaskOffline(taskId: string): Promise<void> {
  await deleteItem(STORES.TASKS, taskId);
}

/**
 * Get all tasks for a board
 */
export async function getTasksByBoard(boardId: string): Promise<OfflineTask[]> {
  return getItemsByIndex<OfflineTask>(STORES.TASKS, 'boardId', boardId);
}

/**
 * Get tasks created offline that need syncing
 */
export async function getOfflineTasks(): Promise<OfflineTask[]> {
  return getItemsByIndex<OfflineTask>(STORES.TASKS, 'isOffline', true as unknown as IDBValidKey);
}

/**
 * Mark a task as synced
 */
export async function markTaskSynced(taskId: string): Promise<void> {
  const task = await getItem<OfflineTask>(STORES.TASKS, taskId);
  if (task) {
    await setItem(STORES.TASKS, {
      ...task,
      isOffline: false,
      syncedAt: Date.now(),
    });
  }
}

// ============================================================================
// Board-specific operations
// ============================================================================

/**
 * Save a board to offline storage
 */
export async function saveBoardOffline(board: OfflineBoard): Promise<void> {
  await setItem(STORES.BOARDS, {
    ...board,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Get all boards for a project
 */
export async function getBoardsByProject(
  projectId: string
): Promise<OfflineBoard[]> {
  return getItemsByIndex<OfflineBoard>(STORES.BOARDS, 'projectId', projectId);
}

// ============================================================================
// Metadata operations
// ============================================================================

interface MetadataItem {
  key: string;
  value: unknown;
}

/**
 * Get metadata value
 */
export async function getMetadata<T>(key: string): Promise<T | undefined> {
  const item = await getItem<MetadataItem>(STORES.METADATA, key);
  return item?.value as T | undefined;
}

/**
 * Set metadata value
 */
export async function setMetadata<T>(key: string, value: T): Promise<void> {
  await setItem(STORES.METADATA, { key, value });
}

/**
 * Get last sync timestamp
 */
export async function getLastSyncTime(): Promise<number | undefined> {
  return getMetadata<number>('lastSyncTime');
}

/**
 * Set last sync timestamp
 */
export async function setLastSyncTime(timestamp: number): Promise<void> {
  return setMetadata('lastSyncTime', timestamp);
}

// ============================================================================
// Cleanup operations
// ============================================================================

/**
 * Remove old synced data to save space
 */
export async function cleanupOldData(maxAge: number = 7 * 24 * 60 * 60 * 1000): Promise<void> {
  const cutoffTime = Date.now() - maxAge;
  const database = await getDb();

  // Remove old tasks
  const transaction = database.transaction(STORES.TASKS, 'readwrite');
  const store = transaction.objectStore(STORES.TASKS);
  const index = store.index('localUpdatedAt');
  const range = IDBKeyRange.upperBound(cutoffTime);

  return new Promise((resolve, reject) => {
    const request = index.openCursor(range);

    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        const task = cursor.value as OfflineTask;
        // Only delete if synced and not created offline
        if (!task.isOffline && task.syncedAt) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Get storage usage statistics
 */
export async function getStorageStats(): Promise<{
  tasks: number;
  boards: number;
  syncQueue: number;
  estimatedSize: number;
}> {
  const tasks = await getAllItems(STORES.TASKS);
  const boards = await getAllItems(STORES.BOARDS);
  const syncQueue = await getAllItems(STORES.SYNC_QUEUE);

  // Rough estimate of storage size
  const estimatedSize =
    JSON.stringify(tasks).length +
    JSON.stringify(boards).length +
    JSON.stringify(syncQueue).length;

  return {
    tasks: tasks.length,
    boards: boards.length,
    syncQueue: syncQueue.length,
    estimatedSize,
  };
}
